import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Upload, X, Paperclip } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * Public enquiry page — NO login required.
 * Available at /enquiry. Mobile-first single column.
 * The API is public: POST /api/enquiry/public (with honeypot + IP rate limit).
 */

const REQUIREMENTS = ["Kitchen", "Wardrobes", "Vanity", "Only Carcass", "Others"];
const TIMELINES = [
  { value: "Immediate", label: "Immediate" },
  { value: "1 month", label: "Within 1 month" },
  { value: "2-3 months", label: "2 – 3 months" },
  { value: "3+ months", label: "3+ months" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const publicApi = axios.create({ baseURL: API_BASE, withCredentials: false });

const ALLOWED_EXTS = [".jpg", ".jpeg", ".png", ".pdf", ".dwg", ".dxf"];

export default function EnquiryPublic() {
  const [state, setState] = useState({
    name: "", phone: "", email: "", address: "",
    requirements: [], timeline: "", notes: "",
    website: "", // honeypot — must stay empty
  });
  const [files, setFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const toggleReq = (r) => {
    setState((s) => ({
      ...s,
      requirements: s.requirements.includes(r)
        ? s.requirements.filter((x) => x !== r)
        : [...s.requirements, r],
    }));
  };

  const addFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError("");
    // Client-side allowlist: bail before we even upload
    const bad = Array.from(fileList).filter((f) => {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      return !ALLOWED_EXTS.includes(ext);
    });
    if (bad.length > 0) {
      setError(`Unsupported file type: ${bad.map((f) => f.name).join(", ")}. Allowed: ${ALLOWED_EXTS.join(", ")}`);
      return;
    }
    setUploading(true);
    const newFiles = Array.from(fileList);
    try {
      const newIds = [];
      for (const f of newFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await publicApi.post("/enquiry/public/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push({ id: data.id, name: data.name, size: data.size });
      }
      setFiles((prev) => [...prev, ...newFiles]);
      setUploadedFileIds((prev) => [...prev, ...newIds]);
    } catch (e) {
      setError(e?.response?.data?.detail || "File upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setUploadedFileIds((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!state.name.trim() || !state.phone.trim()) {
      setError("Name and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...state,
        email: state.email || undefined,
        file_ids: uploadedFileIds.map((f) => f.id),
      };
      const { data } = await publicApi.post("/enquiry/public", payload);
      setSuccess({ enquiry_id: data.enquiry_id });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return <ThankYou enquiry_id={success.enquiry_id} />;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="enquiry-public">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="mb-8">
          <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Get a quote</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]" style={{ fontFamily: "Chivo" }}>
            Let&apos;s Transform Your Space with Premium Steel Fitouts.
          </h1>
          <p className="text-slate-600 mt-3 max-w-lg">
            Tell us a bit about your project and one of our designers will call you back within 24 hours.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5 bg-white border border-slate-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <Field label="Your name" required>
            <input type="text" required data-testid="enquiry-name" className={FIELD}
              value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })}
              placeholder="e.g. Priya Iyer" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone" required>
              <input type="tel" required data-testid="enquiry-phone" className={FIELD}
                value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })}
                placeholder="+91 98765 43210" />
            </Field>
            <Field label="Email">
              <input type="email" data-testid="enquiry-email" className={FIELD}
                value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })}
                placeholder="you@example.com" />
            </Field>
          </div>

          <Field label="Site address">
            <textarea rows={2} data-testid="enquiry-address" className={FIELD}
              value={state.address} onChange={(e) => setState({ ...state, address: e.target.value })}
              placeholder="Flat / house, area, city, PIN" />
          </Field>

          <Field label="What are you looking for?">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {REQUIREMENTS.map((r) => (
                <label key={r} className={`flex items-center gap-2 border rounded-sm px-3 py-2.5 cursor-pointer text-sm select-none ${state.requirements.includes(r) ? "bg-orange-50 border-orange-500 text-orange-900" : "border-slate-300 text-slate-700 hover:border-slate-400"}`}>
                  <input type="checkbox" className="w-4 h-4 accent-orange-500"
                    checked={state.requirements.includes(r)}
                    onChange={() => toggleReq(r)}
                    data-testid={`enquiry-req-${r.toLowerCase().replace(/\s+/g, "-")}`} />
                  {r}
                </label>
              ))}
            </div>
          </Field>

          <Field label="When do you need it?">
            <select className={FIELD} data-testid="enquiry-timeline"
              value={state.timeline} onChange={(e) => setState({ ...state, timeline: e.target.value })}>
              <option value="">— Select a timeline —</option>
              {TIMELINES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>

          <Field label="Upload drawings (optional)">
            <label htmlFor="enquiry-files" className="flex items-center gap-3 border border-dashed border-slate-300 rounded-sm p-4 cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 transition-colors">
              {uploading ? <Loader2 className="w-5 h-5 text-orange-500 animate-spin" /> : <Upload className="w-5 h-5 text-slate-500" />}
              <div>
                <div className="text-sm font-semibold text-slate-800">{uploading ? "Uploading…" : "Click or drop files here"}</div>
                <div className="text-xs text-slate-500">JPG, PNG, PDF, DWG, DXF · up to 15 MB each</div>
              </div>
              <input id="enquiry-files" data-testid="enquiry-files" type="file" multiple hidden
                onChange={(e) => addFiles(e.target.files)}
                accept=".jpg,.jpeg,.png,.pdf,.dwg,.dxf,image/jpeg,image/png,application/pdf" />
            </label>
            {files.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {files.map((f, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0"><Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{f.name}</span>
                      <span className="text-xs text-slate-400 shrink-0">{Math.round(f.size / 1024)} KB</span>
                    </span>
                    <button type="button" onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-600 shrink-0"><X className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </Field>

          <Field label="Notes">
            <textarea rows={3} data-testid="enquiry-notes" className={FIELD}
              value={state.notes} onChange={(e) => setState({ ...state, notes: e.target.value })}
              placeholder="Anything else you'd like us to know?" />
          </Field>

          {/* Honeypot — hidden from users, catches bots */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
            <label>Website (leave blank)
              <input type="text" tabIndex={-1} autoComplete="off"
                value={state.website} onChange={(e) => setState({ ...state, website: e.target.value })} />
            </label>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" role="alert">{error}</div>
          )}

          <button type="submit" disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-sm disabled:opacity-60"
            data-testid="enquiry-submit">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Submit enquiry"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">By submitting you agree to be contacted by our team.</p>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function Header() {
  return (
    <header className="bg-white border-b border-slate-200 px-4 py-3">
      <div className="max-w-2xl mx-auto flex items-center gap-3">
        <Logo className="h-11 w-11 shrink-0" data-testid="public-logo" />
        <div className="flex items-baseline gap-1.5 font-black text-lg leading-tight uppercase tracking-tight" style={{ fontFamily: "Chivo" }}>
          <span style={{ color: "#E52B50" }}>Hygiene</span>
          <span className="text-slate-900">Kitchens</span>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 py-6 mt-10">
      <div className="max-w-2xl mx-auto px-4 text-xs text-slate-500 text-center" data-testid="public-footer">
        © Hygiene Kitchens · Bengaluru, India · Manufacturer of Premium Modern Steel Kitchens, Wardrobes and other Furnishings
      </div>
    </footer>
  );
}

function ThankYou({ enquiry_id }) {
  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="enquiry-thankyou">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={2.25} />
        </div>
        <div className="hk-tiny-label" style={{ color: "#E52B50" }}>All set</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mt-1" style={{ fontFamily: "Chivo" }}>
          Thanks — we&apos;ll be in touch.
        </h1>
        <p className="text-slate-600 mt-4 max-w-md mx-auto">
          Your enquiry <span className="font-mono font-bold text-slate-800">{enquiry_id}</span> has been received.
          One of our designers will call you within 24 hours.
        </p>
        <a href="/enquiry" className="mt-8 inline-block text-orange-600 hover:text-orange-700 font-bold text-sm">Submit another →</a>
      </main>
      <Footer />
    </div>
  );
}

const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";
